/**
 * Reusable stock-symbol search-and-select input, extracted from the inline
 * `Autocomplete` block that used to live only in `PortfolioTrackerPage.tsx`'s
 * "Add Stock" dialog. Debounces user input and calls
 * `stockMarketService.searchStocks` (`GET /api/v1/stocks/search`), which
 * searches the FULL NSE/BSE catalog by symbol prefix or company-name
 * substring — unlike `listStocks()`, which only returns the smaller active
 * universe and isn't suitable for a type-ahead box.
 *
 * `freeSolo` is on by default (same as Portfolio Tracker's original) so a
 * user can type/commit a symbol this box hasn't matched yet (e.g. one not
 * indexed by name the way they typed it).
 *
 * Typed text (`inputValue`, local state) is kept deliberately SEPARATE from
 * the committed `value`/`onChange` prop: `onChange` only fires when the
 * user actually selects an option, presses Enter, or blurs the field --
 * never on every keystroke. This matters here specifically because at
 * least one consumer (F&O Trade Analysis) fetches/persists against
 * `value` on every change -- firing `onChange` per keystroke used to fetch
 * (and 404) for every partial symbol ("B", "BS", "BSE", ...) while the
 * user was still typing, which is what made the search box look broken.
 * Portfolio Tracker's original inline version didn't have this bug only
 * because its consumer was a form field, not a live fetch trigger.
 */
import { useEffect, useState } from "react";
import { Autocomplete, TextField, type TextFieldProps } from "@mui/material";
import { stockMarketService } from "@/services/stockMarketService";
import type { StockMaster } from "@/types";

interface StockSymbolAutocompleteProps {
  value: string;
  onChange: (symbol: string) => void;
  label?: string;
  helperText?: string;
  disabled?: boolean;
  size?: TextFieldProps["size"];
  sx?: TextFieldProps["sx"];
}

export function StockSymbolAutocomplete({
  value,
  onChange,
  label = "Stock Symbol",
  helperText,
  disabled,
  size = "small",
  sx,
}: StockSymbolAutocompleteProps) {
  // What's currently typed/shown in the box -- separate from `value` (see
  // module docstring). Re-synced from `value` when it changes from outside
  // (e.g. a `?fnoSymbol=` deep link, or another control resetting it).
  const [inputValue, setInputValue] = useState(value);
  const [options, setOptions] = useState<StockMaster[]>([]);

  useEffect(() => {
    setInputValue(value);
  }, [value]);

  useEffect(() => {
    const query = inputValue.trim();
    if (query.length < 1) {
      setOptions([]);
      return;
    }
    const handle = setTimeout(() => {
      stockMarketService
        .searchStocks(query, 20)
        .then((res) => setOptions(res.items))
        .catch(() => setOptions([]));
    }, 250);
    return () => clearTimeout(handle);
  }, [inputValue]);

  const commit = (raw: string) => {
    const next = raw.trim().toUpperCase();
    if (next && next !== value) onChange(next);
  };

  return (
    <Autocomplete
      freeSolo
      disabled={disabled}
      options={options.map((s) => s.symbol)}
      inputValue={inputValue}
      onInputChange={(_e, next) => setInputValue(next)}
      onChange={(_e, selected) => {
        if (typeof selected === "string") commit(selected);
      }}
      onBlur={() => commit(inputValue)}
      renderOption={(props, option) => {
        const known = options.find((s) => s.symbol === option);
        return (
          <li {...props} key={option}>
            {known ? `${known.symbol} — ${known.company_name}` : option}
          </li>
        );
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          helperText={helperText}
          size={size}
          sx={sx}
          onKeyDown={(e) => {
            if (e.key === "Enter") commit(inputValue);
          }}
        />
      )}
    />
  );
}

export default StockSymbolAutocomplete;
